// src/components/embed/wpBridge.tsx
import { useEffect, useMemo } from "react";

/**
 * Minimal postMessage bridge between React (iFrame) and WordPress (parent).
 * - No UI. No routing changes. Does not touch your existing auth logic.
 * - You provide 2 callbacks: getAuthSnapshot() and requestLogout().
 *
 * Parent ⇄ Child message types:
 *  - REQUEST_AUTH_STATE (parent→child)
 *  - AUTH_STATE          (child→parent)  payload: { loggedIn: boolean, displayName?: string | null }
 *  - LOGOUT_REQUEST      (parent→child)
 *  - LOGOUT_DONE         (child→parent)
 *
 * Optional: Dispatch a CustomEvent('auth:state-changed', { detail: snapshot })
 * from your existing login/logout code to proactively push state to WP.
 */

type AuthSnapshot = { loggedIn: boolean; displayName?: string | null };

type BridgeMessage =
  | { type: "REQUEST_AUTH_STATE" }
  | { type: "AUTH_STATE"; payload: AuthSnapshot }
  | { type: "LOGOUT_REQUEST" }
  | { type: "LOGOUT_DONE" };

type WpBridgeProps = {
  /** Exact origin of your WordPress parent, e.g. "https://emasajid.moia.gov.sa" */
  parentOrigin: string;

  /** Enable bridge (use with ?embed=1). Default: true */
  enabled?: boolean;

  /** Read current auth state from your existing auth service */
  getAuthSnapshot: () => AuthSnapshot;

  /** Call your existing logout; may be async */
  requestLogout: () => Promise<void> | void;

  /** Optional: log to console for troubleshooting */
  debug?: boolean;

  /** Optional: custom event name to listen for auth changes. Default: "auth:state-changed" */
  authChangedEventName?: string;
};

function log(debug: boolean | undefined, ...args: any[]) {
  if (debug) console.log("[WpBridge]", ...args);
}

export default function WpBridge({
  parentOrigin,
  enabled = true,
  getAuthSnapshot,
  requestLogout,
  debug,
  authChangedEventName = "auth:state-changed",
}: WpBridgeProps) {
  const isParentDifferent = useMemo(() => {
    try {
      return window.parent && window.parent !== window;
    } catch {
      return false;
    }
  }, []);

  useEffect(() => {
    if (!enabled) {
      log(debug, "Disabled; bridge not active.");
      return;
    }
    if (!isParentDifferent) {
      log(debug, "No parent frame detected; bridge not active.");
      return;
    }
    if (!parentOrigin || parentOrigin === "*" || !/^https?:\/\//.test(parentOrigin)) {
      log(debug, "Invalid parentOrigin. Bridge not active.", parentOrigin);
      return;
    }

    const send = (msg: BridgeMessage) => {
      try {
        window.parent.postMessage(msg, parentOrigin);
        log(debug, "→ parent", msg);
      } catch (e) {
        log(debug, "postMessage failed", e);
      }
    };

    const sendAuthState = () => {
      const snapshot = safeSnapshot(getAuthSnapshot);
      send({ type: "AUTH_STATE", payload: snapshot });
    };

    const onMessage = (event: MessageEvent<BridgeMessage>) => {
      // Security: only accept messages from the configured parent origin
      if (event.origin !== parentOrigin) return;
      if (event.source !== window.parent) return;

      log(debug, "← parent", event.data);

      switch (event.data?.type) {
        case "REQUEST_AUTH_STATE":
          sendAuthState();
          break;

        case "LOGOUT_REQUEST":
          Promise.resolve(requestLogout())
            .catch((err) => log(debug, "requestLogout error", err))
            .finally(() => {
              send({ type: "LOGOUT_DONE" });
              // Also push the (likely loggedOut) state snapshot
              sendAuthState();
            });
          break;

        default:
          // ignore unknown types
          break;
      }
    };

    // Proactively announce state once after mount (gives instant header update in WP)
    const announceTimer = window.setTimeout(sendAuthState, 50);

    window.addEventListener("message", onMessage);

    // Listen for local auth changes (dispatch this from your existing login/logout)
    const onAuthChanged = (e: Event) => {
      // When you dispatch, include the snapshot in detail. If missing, we recompute.
      const detailSnapshot =
        (e as CustomEvent<AuthSnapshot>)?.detail || safeSnapshot(getAuthSnapshot);
      send({ type: "AUTH_STATE", payload: detailSnapshot });
    };
    window.addEventListener(authChangedEventName, onAuthChanged as EventListener);

    return () => {
      window.clearTimeout(announceTimer);
      window.removeEventListener("message", onMessage);
      window.removeEventListener(authChangedEventName, onAuthChanged as EventListener);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, parentOrigin, isParentDifferent]);

  return null; // Nothing to render
}

/** Guard against exceptions inside getAuthSnapshot to avoid breaking the bridge */
function safeSnapshot(getter: () => AuthSnapshot): AuthSnapshot {
  try {
    const s = getter?.();
    return {
      loggedIn: !!s?.loggedIn,
      displayName: s?.displayName ?? null,
    };
  } catch {
    return { loggedIn: false, displayName: null };
  }
}
